import React from 'react';

const CallToActionBanner = () => {
  return (
    <section className="relative py-24 overflow-hidden">
      {/* Background color */}
      <div className="absolute inset-0 bg-white z-0"></div>

      {/* Green Wave Pattern */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '100%',
          opacity: 1,
          zIndex: 1,
          pointerEvents: 'none',
          overflow: 'hidden'
        }}
      >
        <svg width="200%" height="100%" viewBox="0 0 2880 400" preserveAspectRatio="none">
          <defs>
            <linearGradient id="ctaWaveGradient1" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#A7D7A4" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#A7D7A4" stopOpacity="0.05" />
            </linearGradient>
            <linearGradient id="ctaWaveGradient2" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#A7D7A4" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#A7D7A4" stopOpacity="0.02" />
            </linearGradient>
            <linearGradient id="ctaWaveGradient3" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#A7D7A4" stopOpacity="0.2" />
              <stop offset="100%" stopColor="#A7D7A4" stopOpacity="0.01" />
            </linearGradient>
          </defs>
          
          {/* Wave 1 - Bottom layer */}
          <path 
            d="M0,150 
               C240,200 480,100 720,150 
               C960,200 1200,100 1440,150 
               C1680,200 1920,100 2160,150 
               C2400,200 2640,100 2880,150 
               L2880,400 L0,400 Z" 
            fill="url(#ctaWaveGradient1)" 
            className="cta-wave cta-wave-1"
          />
          
          {/* Wave 2 - Middle layer */}
          <path 
            d="M0,120 
               C240,80 480,160 720,120 
               C960,80 1200,160 1440,120 
               C1680,80 1920,160 2160,120 
               C2400,80 2640,160 2880,120 
               L2880,400 L0,400 Z" 
            fill="url(#ctaWaveGradient2)" 
            className="cta-wave cta-wave-2"
          />
          
          {/* Wave 3 - Top layer */}
          <path 
            d="M0,100 
               C180,140 360,60 540,100 
               C720,140 900,60 1080,100 
               C1260,140 1440,60 1620,100 
               C1800,140 1980,60 2160,100 
               C2340,140 2520,60 2700,100
               C2780,120 2840,90 2880,100
               L2880,400 L0,400 Z" 
            fill="url(#ctaWaveGradient3)" 
            className="cta-wave cta-wave-3"
          />
          
          {/* Wave Outline for Definition */}
          <path 
            d="M0,100 
               C180,140 360,60 540,100 
               C720,140 900,60 1080,100 
               C1260,140 1440,60 1620,100 
               C1800,140 1980,60 2160,100 
               C2340,140 2520,60 2700,100
               C2780,120 2840,90 2880,100" 
            fill="none" 
            stroke="#A7D7A4" 
            strokeWidth="1.5"
            opacity="0.6"
            className="cta-wave cta-wave-3"
          />
        </svg>
      </div>

      {/* Wave Animations */}
      <style dangerouslySetInnerHTML={{ __html: `
        /* Wave animations with different speeds and directions */
        .cta-wave-1 {
          animation: ctaWaveMovement1 20s linear infinite;
        }
        
        .cta-wave-2 {
          animation: ctaWaveMovement2 15s linear infinite;
        }
        
        .cta-wave-3 {
          animation: ctaWaveMovement3 12s linear infinite;
        }
        
        @keyframes ctaWaveMovement1 {
          from {
            transform: translateX(0);
          }
          to {
            transform: translateX(-50%);
          }
        }
        
        @keyframes ctaWaveMovement2 {
          from {
            transform: translateX(-50%);
          }
          to {
            transform: translateX(0);
          }
        }
        
        @keyframes ctaWaveMovement3 {
          from {
            transform: translateX(0);
          }
          to {
            transform: translateX(-50%);
          }
        }
      `}} />

      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main content */}
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-12">
          {/* Left side */}
          <div className="flex-1 max-w-2xl">
            <h2 className="text-3xl sm:text-4xl md:text-5xl text-[#305248] font-light mb-6">
              Transform Your Life Today
            </h2>
            <p className="text-gray-700 text-lg md:text-xl mb-8">
              Join our wellness program and experience the perfect blend of ancient wisdom and modern science.
            </p>
            <div className="flex flex-wrap gap-6">
              <a 
                href="https://calendly.com/jaganmohan97"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  backgroundColor: '#305248',
                  color: 'white',
                  padding: '10px 40px',
                  borderRadius: '8px',
                  fontWeight: '500',
                  fontSize: '16px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  textDecoration: 'none',
                  display: 'inline-block'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#2a453f'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#305248'}
              >
                Book Consultation
              </a>
            </div>
          </div>

          {/* Right side - Stats */}
          <div className="grid grid-cols-2 gap-6 w-full lg:w-auto lg:min-w-[400px]">
            <div className="bg-white bg-opacity-95 rounded-xl p-6 shadow-lg">
              <div className="text-4xl font-light mb-2 text-[#305248]">98%</div>
              <div className="text-gray-700 font-medium">Client Satisfaction</div>
            </div>
            <div className="bg-white bg-opacity-95 rounded-xl p-6 shadow-lg">
              <div className="text-4xl font-light mb-2 text-[#305248]">5k+</div>
              <div className="text-gray-700 font-medium">Success Stories</div>
            </div>
            <div className="bg-white bg-opacity-95 rounded-xl p-6 shadow-lg">
              <div className="text-4xl font-light mb-2 text-[#305248]">15+</div>
              <div className="text-gray-700 font-medium">Expert Coaches</div>
            </div>
            <div className="bg-white bg-opacity-95 rounded-xl p-6 shadow-lg">
              <div className="text-4xl font-light mb-2 text-[#305248]">24/7</div>
              <div className="text-gray-700 font-medium">Support</div>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-white bg-opacity-95 rounded-xl p-8 shadow-lg">
            <div className="flex items-center gap-5">
              <div className="w-14 h-14 bg-[#305248] rounded-full flex items-center justify-center">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="font-medium text-xl text-[#305248]">Personalized Plans</h3>
                <p className="text-gray-600 mt-2">Customized to your goals</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white bg-opacity-95 rounded-xl p-8 shadow-lg">
            <div className="flex items-center gap-5">
              <div className="w-14 h-14 bg-[#305248] rounded-full flex items-center justify-center">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="font-medium text-xl text-[#305248]">Flexible Schedule</h3>
                <p className="text-gray-600 mt-2">Train at your convenience</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white bg-opacity-95 rounded-xl p-8 shadow-lg">
            <div className="flex items-center gap-5">
              <div className="w-14 h-14 bg-[#305248] rounded-full flex items-center justify-center">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <h3 className="font-medium text-xl text-[#305248]">Instant Access</h3>
                <p className="text-gray-600 mt-2">Start immediately</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CallToActionBanner; 